// Branded string types to prevent accidental misuse
export type ISODateString = string & { readonly __brand: 'ISODate' }; // YYYY-MM-DD
export type PeriodMonth = string & { readonly __brand: 'PeriodMonth' };  // YYYY-MM
export type SharePointId = string & { readonly __brand: 'SPId' };

export function toISODateString(s: string): ISODateString {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) throw new Error(`Invalid ISO date: ${s}`);
  return s as ISODateString;
}

export function toPeriodMonth(s: string): PeriodMonth {
  if (!/^\d{4}-\d{2}$/.test(s)) throw new Error(`Invalid period month: ${s}`);
  return s as PeriodMonth;
}

// ─── Enums ────────────────────────────────────────────────────────────────────

export enum TimesheetStatus {
  Draft = 'Draft',
  Submitted = 'Submitted',
  ManagerApproved = 'Manager Approved',
  SuperApproved = 'Super Approved',
  Locked = 'Locked',
  Rejected = 'Rejected',
  ReadOnly = 'Read-Only', // bank holidays
}

export enum ProjectType {
  RD = 'R&D',
  GA = 'G&A',
  Leave = 'Leave',
  Commercial = 'Commercial',
  Standard = 'Standard',
}

export enum CostType {
  Fixed = 'Fixed',
  Variable = 'Variable',
}

export enum PersonRole {
  Colleague = 'Colleague',
  LineManager = 'Line Manager',
  PMOLead = 'PMO Lead / Super Admin',
  SystemAdministrator = 'System Administrator',
}

export enum BankHolidayRegion {
  UK = 'UK',
  EU = 'EU',
  All = 'All',
}

export enum ProjectStatus {
  Active = 'Active',
  Closed = 'Closed',
  Archived = 'Archived',
}

// ─── Domain Models ────────────────────────────────────────────────────────────

export interface TimeEntry {
  readonly id: SharePointId;
  /** Person's M365 email — text column, used for filtering. */
  personEmail: string;
  /** Person's display name — text column. */
  personName: string;
  projectCode: string;
  dateWorked: ISODateString;
  /** Auto-computed by SharePoint calculated column. Never written by client. */
  readonly periodMonth: PeriodMonth;
  hours: number;
  status: TimesheetStatus;
  managerName: string;
  managerEmail: string;
  /** SharePoint site-user numeric ID for the Manager Person field. */
  managerId: number | null;
  managerApprovedDate: ISODateString | null;
  superApproverName: string;
  superApproverEmail: string;
  /** SharePoint site-user numeric ID for the Super Approver Person field. */
  superApproverId: number | null;
  superApprovedDate: ISODateString | null;
  submittedDate: ISODateString | null;
  lockedDate: ISODateString | null;
  unlockedDate: ISODateString | null;
  unlockReason: string;
  unlockApprovedBy: string;
  rejectionNote: string;
  /** Snapshot at time of entry — immutable for audit. */
  billRate: number | null;
  /** Snapshot at time of entry — immutable for audit. */
  costRate: number | null;
  taskDescription: string;
  billableToClient: boolean;
  assetCode: string;
  readonly created: ISODateString;
  readonly modified: ISODateString;
}

/** Shape for new entries before SharePoint assigns an ID. */
export type NewTimeEntry = Omit<TimeEntry, 'id' | 'periodMonth' | 'created' | 'modified'> & {
  id: `new-${string}`;
};

export function isNewEntry(entry: TimeEntry | NewTimeEntry): entry is NewTimeEntry {
  return entry.id.startsWith('new-');
}

/** Audit record for tracking edits and unlocks made by Unlock Group members. */
export interface TimeEntryAuditRecord {
  readonly id?: SharePointId;
  timeEntryId: number; // SP ID of edited entry
  personName: string;
  personEmail: string;
  dateWorked: ISODateString;
  periodMonth: PeriodMonth;
  oldValue: string | null;
  newValue: string | null;
  editorName: string;
  editorEmail: string;
  editedDate: ISODateString;
  editReasonNotes: string;
}

export interface PersonOption {
  name: string;
  email: string;
}

export interface Person {
  readonly id: SharePointId;
  fullName: string;
  m365Email: string;
  department: string;
  role: PersonRole;
  lineManager: string;
  lineManagerEmail: string;
  backupApprover: string;
  backupApproverEmail: string;
  superApprover: string;
  superApproverEmail: string;
  costType: CostType;
  hourlyRate: number | null;
  exemptFromReminders: boolean;
  exemptionStart: ISODateString | null;
  exemptionEnd: ISODateString | null;
  isTimesheetMgmtGroup: boolean;
  active: boolean;
  aadObjectId: string;
  // Ordering-specific fields
  purchaseApprovalLimit: number | null;
  isChemicalApprover: boolean;
  isOrderMgmtGroup: boolean;
  isCFO: boolean;
  canApproveOnBehalfOfCEO: boolean;
}

export interface Project {
  readonly id: SharePointId;
  projectCode: string;
  projectName: string;
  type: ProjectType;
  costCentre: string;
  projectOwnerName: string;
  projectOwnerEmail: string;
  assignedPMName: string;
  assignedPMEmail: string;
  startDate: ISODateString;
  endDate: ISODateString | null;
  estimatedHours: number | null;
  /** Parsed from comma-separated text field. */
  assignedTeamEmails: readonly string[];
  /** Parsed from comma-separated text field. */
  assignedTeamNames: readonly string[];
  isResearchDevelopment: boolean;
  status: ProjectStatus;
  description: string;
}

export interface BankHoliday {
  readonly id: SharePointId;
  holidayDate: ISODateString;
  holidayName: string;
  appliesTo: BankHolidayRegion;
  defaultHours: number;
  year: number;
  isActive: boolean;
}

export interface Asset {
  readonly id: SharePointId;
  title: string;
  description: string;
  responsiblePerson: string;
  responsiblePersonEmail: string;
  responsiblePersonName: string;
  responsiblePeopleEmails: string[];
  responsiblePeopleNames: string[];
  active: boolean;
}

export interface BillOutRate {
  readonly id: SharePointId;
  /** Name of the cost group (e.g. "Senior Developer", "Project Manager"). */
  costGroup: string;
  /** Billable rate charged to clients (£/hr). */
  billRate: number;
  /** Internal cost rate paid to employees (£/hr). */
  costRate: number;
  /** First date this rate is effective (inclusive). */
  effectiveFrom: ISODateString;
  /** Last date this rate is effective (inclusive). Null = open-ended / current rate. */
  effectiveTo: ISODateString | null;
  notes: string;
  readonly created: ISODateString;
  readonly modified: ISODateString;
}

// ─── UI-Specific View Models ──────────────────────────────────────────────────

export interface DayCellMeta {
  date: ISODateString;
  isWeekend: boolean;
  isBankHoliday: boolean;
  bankHolidayName: string;
  isOutOfMonth: boolean;
  isEditable: boolean;
}

export interface MonthSummary {
  periodMonth: PeriodMonth;
  totalHours: number;
  breakdownByType: Record<ProjectType, number>;
  status: TimesheetStatus;
  submittedDate: ISODateString | null;
  allWorkingDaysFilled: boolean;
}
